import { useState, type FormEvent } from 'react';
import { BillableLogo, GoogleMark } from '@/components/brand';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { sendSignInCode, signInWithGoogle, verifySignInCode } from '@/api/session';
import { ApiError } from '@/lib/api';

function messageOf(err: unknown, fallback: string) {
  if (err instanceof ApiError && err.status === 429) return 'Too many tries. Wait a minute and try again.';
  return err instanceof Error && err.message ? err.message : fallback;
}

/**
 * Google, or a code sent to your email. The code is for addresses with no
 * Google account behind them. On success it reloads into `redirectTo`, so the
 * whole app starts from the new session rather than a stale 401.
 */
export function SignInCard({
  redirectTo = '/timesheet',
  initialEmail = ''
}: {
  redirectTo?: string;
  initialEmail?: string;
}) {
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendCode = async (e?: FormEvent) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await sendSignInCode(email.trim());
      setCode('');
      setStep('code');
    } catch (err) {
      setError(messageOf(err, 'Could not send a code.'));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await verifySignInCode(email.trim(), code.trim());
      window.location.assign(redirectTo);
    } catch (err) {
      setError(messageOf(err, 'That code did not work.'));
      setBusy(false);
    }
  };

  return (
    <div className="w-full max-w-sm rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-8 text-center shadow-sm">
      <BillableLogo className="mx-auto h-12 w-12" />
      <h1 className="mt-4 text-2xl font-semibold tracking-tight">Billable</h1>

      {step === 'email' ? (
        <>
          <Button variant="google" className="mt-6 w-full" onClick={() => signInWithGoogle(redirectTo)}>
            <GoogleMark className="h-[18px] w-[18px]" />
            Continue with Google
          </Button>
          <div className="my-5 flex items-center gap-3 text-xs text-[var(--text-subtle)]">
            <span className="h-px flex-1 bg-[var(--line)]" />
            or
            <span className="h-px flex-1 bg-[var(--line)]" />
          </div>
          <form onSubmit={sendCode} className="space-y-3 text-left">
            <Input
              type="email"
              required
              autoComplete="email"
              placeholder="you@yourfirm.com"
              aria-label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button type="submit" className="w-full" disabled={busy || !email.trim()}>
              Email me a code
            </Button>
          </form>
        </>
      ) : (
        <form onSubmit={verify} className="mt-6 space-y-3 text-left">
          <p className="text-center text-sm text-[var(--text-muted)]">
            Enter the code we sent to <span className="font-medium text-[var(--text)]">{email.trim()}</span>
          </p>
          <Input
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={6}
            placeholder="123456"
            aria-label="Sign-in code"
            className="h-11 text-center font-mono text-lg tracking-[0.4em]"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          />
          <Button type="submit" variant="primary" className="w-full" disabled={busy || code.length !== 6}>
            Sign in
          </Button>
          <div className="flex justify-between text-xs">
            <button type="button" className="text-[var(--text-muted)] hover:text-[var(--text)]" onClick={() => setStep('email')}>
              Use a different email
            </button>
            <button type="button" className="text-[var(--text-muted)] hover:text-[var(--text)]" disabled={busy} onClick={() => void sendCode()}>
              Send a new code
            </button>
          </div>
        </form>
      )}

      {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}
